/**
 * Density + Material-Classification card — rendered inside BlockModelTab.
 *
 * UI:
 *   - Default bulk density
 *   - Ore / Waste density overrides (fastest common-case override)
 *   - Per-lithology density + material-type override table, auto-populated
 *     from the project's actual logged lithologies (via
 *     GET /projects/:id/detected-lithologies). Real deposits have multiple
 *     rock types with genuinely different densities; a single global value
 *     is a simplification real resource estimation shouldn't rely on.
 *   - Save + Apply-classification buttons — the latter re-classifies the
 *     active block model and shows the rollup right below the card.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { Layers, Save, PlayCircle, Palette, RefreshCw } from "lucide-react";
import MaterialClassManager from "@/components/MaterialClassManager";
import { OreTonnage } from "@/components/ssot/OreFigures";
import useAutoFill from "@/hooks/useAutoFill";
import AutoFilledHint from "@/components/AutoFilledHint";

// Common material-type default options for the per-lithology mapping.
// This is a *starting default* per rock type — the grade-driven ClassRule
// engine still runs and can override on a per-block basis.
const MATERIAL_TYPE_OPTIONS = [
  "", // "(auto — let rules decide)"
  "ore",
  "low_grade",
  "waste",
  "overburden",
  "topsoil",
  "unmineralised",
];

export default function DensityClassPanel({ projectId, refreshKey }) {
  const [cfg, setCfg] = useState({
    default_bulk: 2.7, kind: "bulk",
    density_by_ore_type: { ore: 2.85, waste: 2.65 },
    density_by_lithology: {}, density_by_domain: {}, density_by_weathering: {},
    material_type_by_lithology: {},
  });
  const [detected, setDetected] = useState([]);        // [{name, drillhole_count, block_count}]
  const [detectedBusy, setDetectedBusy] = useState(false);
  const [rollup, setRollup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [classMgrOpen, setClassMgrOpen] = useState(false);
  // Auto-fill hook — lithology_codes suggestion from actual drillhole logs
  const auto = useAutoFill(projectId, { sourceStamp: refreshKey });
  const lithF = auto.field("lithology_codes");

  const loadDetected = () => {
    setDetectedBusy(true);
    api.get(`/projects/${projectId}/detected-lithologies`)
      .then((r) => setDetected(r.data?.lithologies || []))
      .catch(() => setDetected([]))
      .finally(() => setDetectedBusy(false));
  };

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/density`)
      .then((r) => setCfg((prev) => ({ ...prev, ...r.data,
        material_type_by_lithology: r.data?.material_type_by_lithology || {},
        density_by_lithology: r.data?.density_by_lithology || {},
      })))
      .catch(() => {});
    loadDetected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshKey]);

  // Union of detected lithologies + any lithologies already in the saved
  // config (so a lithology the user typed manually still shows up even if
  // it doesn't appear in current drillholes / blocks).
  const lithRows = useMemo(() => {
    const names = new Set(detected.map((d) => d.name));
    Object.keys(cfg.density_by_lithology || {}).forEach((n) => names.add(n));
    Object.keys(cfg.material_type_by_lithology || {}).forEach((n) => names.add(n));
    return Array.from(names).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [detected, cfg.density_by_lithology, cfg.material_type_by_lithology]);

  const setOreTypeVal = (key, val) => {
    const v = parseFloat(val);
    setCfg((c) => ({ ...c, density_by_ore_type: { ...c.density_by_ore_type, [key]: Number.isFinite(v) ? v : 0 } }));
  };

  const setLithDensity = (name, val) => {
    setCfg((c) => {
      const next = { ...(c.density_by_lithology || {}) };
      if (val === "" || val === null || val === undefined) delete next[name];
      else {
        const v = parseFloat(val);
        if (Number.isFinite(v)) next[name] = v; else delete next[name];
      }
      return { ...c, density_by_lithology: next };
    });
  };

  const setLithMaterialType = (name, val) => {
    setCfg((c) => {
      const next = { ...(c.material_type_by_lithology || {}) };
      if (!val) delete next[name];
      else next[name] = val;
      return { ...c, material_type_by_lithology: next };
    });
  };

  const saveDensity = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/density`, cfg);
      toast.success("Density + material overrides saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const applyClassification = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/material-classes/apply`);
      setRollup(data);
      toast.success("Classification recomputed against current density + rules");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Classification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border border-border bg-card p-4 space-y-4"
      data-testid="density-class-panel">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-bold text-base">Density & Material Classification</h3>
      </div>

      <div className="grid grid-cols-3 gap-3 font-mono text-xs">
        <div>
          <div className="smin-label mb-1">Default bulk (t/m³)</div>
          <Input
            type="number" step="0.01"
            data-testid="density-default-bulk"
            value={cfg.default_bulk ?? 2.7}
            onChange={(e) => setCfg({ ...cfg, default_bulk: parseFloat(e.target.value) || 0 })}
            className="rounded-none bg-input font-mono"
          />
        </div>
        <div>
          <div className="smin-label mb-1">Ore density (t/m³)</div>
          <Input
            type="number" step="0.01"
            data-testid="density-ore"
            value={cfg.density_by_ore_type?.ore ?? 2.85}
            onChange={(e) => setOreTypeVal("ore", e.target.value)}
            className="rounded-none bg-input font-mono"
          />
        </div>
        <div>
          <div className="smin-label mb-1">Waste density (t/m³)</div>
          <Input
            type="number" step="0.01"
            data-testid="density-waste"
            value={cfg.density_by_ore_type?.waste ?? 2.65}
            onChange={(e) => setOreTypeVal("waste", e.target.value)}
            className="rounded-none bg-input font-mono"
          />
        </div>
      </div>

      {/* Per-lithology density + material-type override table */}
      <div className="border-t border-border pt-3 space-y-2"
        data-testid="lithology-override-table">
        <div className="flex items-center justify-between">
          <div className="smin-label">
            Per-lithology overrides · {lithRows.length} detected
          </div>
          <button
            onClick={loadDetected}
            disabled={detectedBusy}
            data-testid="lithology-refresh"
            className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-1 transition">
            <RefreshCw className={`h-3 w-3 ${detectedBusy ? "animate-spin" : ""}`} />
            Refresh from drillholes / blocks
          </button>
        </div>
        {lithF && (
          <AutoFilledHint
            reason={lithF.reason}
            source={lithF.source}
          />
        )}
        {lithRows.length === 0 ? (
          <div className="text-[11px] text-muted-foreground italic p-2 border border-dashed border-border rounded">
            No lithologies detected yet — import drillhole lithology intervals or
            run block estimation, then click Refresh. Overrides can also be added
            manually via the Material Rule Manager.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[320px] overflow-y-auto border border-border"
               data-testid="lithology-table-scroll">
            <table className="w-full text-[11px] font-mono tabular-nums">
              <thead className="bg-muted/40 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-2 py-1">Lithology</th>
                  <th className="text-right px-2 py-1 whitespace-nowrap">Bulk density (t/m³)</th>
                  <th className="text-left px-2 py-1">Default material type</th>
                  <th className="text-right px-2 py-1">Sources</th>
                </tr>
              </thead>
              <tbody>
                {lithRows.map((name) => {
                  const meta = detected.find((d) => d.name === name);
                  return (
                    <tr key={name} className="border-t border-border"
                      data-testid={`lithology-row-${name}`}>
                      <td className="px-2 py-1 whitespace-nowrap">{name}</td>
                      <td className="px-2 py-1 text-right">
                        <Input
                          type="number" step="0.01"
                          value={cfg.density_by_lithology?.[name] ?? ""}
                          placeholder={String(cfg.default_bulk ?? 2.7)}
                          onChange={(e) => setLithDensity(name, e.target.value)}
                          data-testid={`lithology-density-${name}`}
                          className="rounded-none bg-input font-mono h-7 text-right w-28 ml-auto"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={cfg.material_type_by_lithology?.[name] || ""}
                          onChange={(e) => setLithMaterialType(name, e.target.value)}
                          data-testid={`lithology-mattype-${name}`}
                          className="rounded-none bg-input font-mono h-7 border border-input px-2 w-full text-[11px]"
                        >
                          <option value="">(auto — rules decide)</option>
                          {MATERIAL_TYPE_OPTIONS.filter(Boolean).map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1 text-right whitespace-nowrap text-muted-foreground text-[10px]">
                        {meta ? (
                          <>
                            {meta.drillhole_count > 0 && <span>{meta.drillhole_count} log</span>}
                            {meta.drillhole_count > 0 && meta.block_count > 0 && <span> · </span>}
                            {meta.block_count > 0 && <span>{meta.block_count.toLocaleString()} blk</span>}
                            {!meta.drillhole_count && !meta.block_count && "—"}
                          </>
                        ) : "(manual)"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-muted-foreground text-[10px] italic">
          Density overrides feed straight into tonnage = size³ × density. Material-type
          is a per-lithology starting default; grade-driven Material Rules still run and
          can override on a per-block basis.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button onClick={saveDensity} disabled={busy}
          data-testid="density-save"
          className="rounded-none font-heading">
          <Save className="h-4 w-4 mr-1.5" /> Save density
        </Button>
        <Button onClick={applyClassification} disabled={busy} variant="outline"
          data-testid="density-apply-classification"
          className="rounded-none font-heading">
          <PlayCircle className="h-4 w-4 mr-1.5" /> Apply classification
        </Button>
        <Button onClick={() => setClassMgrOpen(true)} disabled={busy}
          variant="outline"
          data-testid="open-material-class-manager"
          className="rounded-none font-heading border-primary/40 text-primary hover:bg-primary/10">
          <Palette className="h-4 w-4 mr-1.5" /> Open Material Rule Manager
        </Button>
      </div>

      <MaterialClassManager
        projectId={projectId}
        open={classMgrOpen}
        onOpenChange={setClassMgrOpen}
        onApplied={(data) => setRollup(data)}
      />

      {rollup && (
        <div className="mt-2 border-t border-border pt-3 space-y-3"
          data-testid="classification-rollup">
          {/* One KPI per row so tonnages like "7,080,412.5 t" never overlap
              the adjacent Waste / Strip cells in the narrow sidebar.
              Labels stay short so numbers keep their full precision. */}
          <div className="space-y-2 font-mono text-xs">
            <div className="flex justify-between items-baseline gap-2 border-b border-border pb-1.5">
              <span className="smin-label whitespace-nowrap">Ore</span>
              <OreTonnage
                value={rollup.total_ore_tonnage}
                unit="t"
                source="density-class-rollup"
                className="text-primary text-sm font-semibold tabular-nums whitespace-nowrap"
                data-testid="density-class-ore-tonnage"
              />
            </div>
            <div className="flex justify-between items-baseline gap-2 border-b border-border pb-1.5">
              <span className="smin-label whitespace-nowrap">Waste (+ overburden)</span>
              <OreTonnage
                value={rollup.total_waste_tonnage}
                unit="t"
                source="density-class-rollup"
                className="text-sm tabular-nums whitespace-nowrap"
                data-testid="density-class-waste-tonnage"
              />
            </div>
            <div className="flex justify-between items-baseline gap-2">
              <span className="smin-label whitespace-nowrap">Strip (W:O)</span>
              <span className="text-sm tabular-nums whitespace-nowrap">
                {rollup.strip_ratio ?? "—"}
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] font-mono border border-border tabular-nums">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-2 py-1">Class</th>
                  <th className="text-right px-2 py-1">Blocks</th>
                  <th className="text-right px-2 py-1">Tonnage (t)</th>
                  <th className="text-right px-2 py-1">Avg grade</th>
                  <th className="text-right px-2 py-1 whitespace-nowrap">Metal</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(rollup.rollup || {}).map(([code, agg]) => (
                  <tr key={code} className="border-t border-border">
                    <td className="px-2 py-1 whitespace-nowrap">{code}</td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">{agg.blocks?.toLocaleString()}</td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">{agg.tonnage?.toLocaleString()}</td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">{agg.avg_grade}</td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">{agg.contained_metal?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-muted-foreground text-[10px] italic mt-1">
            Rollups computed from the active block model against the current
            density configuration and default cutoff-based classification rules.
            Change either input and click Apply to recompute.
          </p>
        </div>
      )}
    </div>
  );
}
